import {Injectable} from '@angular/core';
import {EntityState} from '@datorama/akita';
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpResponse} from '@angular/common/http';
import {environment} from '../../environments/environment';
import {ProfilePictureStore} from './profile-picture-store';
import {CurrentUser} from '../user/current-user.service';
import {Observable, of, throwError} from 'rxjs';
import {ProfilePicture} from './profile-picture';
import {catchError, finalize, map, shareReplay, tap} from 'rxjs/operators';

const API_URL = environment.API_URL;

const PRESIGNED_URL_EXPIRES_AT_HEADER = 'X-Presigned-Url-Expires-At';

export const NO_PICTURE_RECHECK_MS = 15 * 60_000;

@Injectable({
  providedIn: 'root'
})
export class ProfilePictureLoadService {

  private readonly inFlight = new Map<string, Observable<ProfilePicture>>();

  constructor(private profilePictureStore: ProfilePictureStore,
              private currentUser: CurrentUser,
              private http: HttpClient) {
  }

  private static toProfilePicture(response: HttpResponse<string>): ProfilePicture {
    const url = response.body || undefined;
    if (!url) {
      return ProfilePictureLoadService.noPicture();
    }
    const expiresHeader = response.headers.get(PRESIGNED_URL_EXPIRES_AT_HEADER);
    const presignedUrlExpiresAt = expiresHeader ? Number(expiresHeader) : undefined;
    return {
      base64: url,
      presignedUrlExpiresAt: Number.isFinite(presignedUrlExpiresAt) ? presignedUrlExpiresAt : undefined
    };
  }

  private static noPicture(): ProfilePicture {
    return {
      base64: undefined,
      loaded: false,
      presignedUrlExpiresAt: Date.now() + NO_PICTURE_RECHECK_MS
    };
  }

  loadProfilePictureForCurrentUser(): Observable<ProfilePicture> {
    return this.loadProfilePicture(this.currentUser.keycloakId);
  }

  loadProfilePicture(userId: string): Observable<ProfilePicture> {
    const running = this.inFlight.get(userId);
    if (running) {
      return running;
    }
    const request = this.http.get(
      API_URL + '/users/' + userId + '/profile-image',
      {
        headers: new HttpHeaders({
          Accept: 'text/plain; charset=UTF-8'
        }),
        responseType: 'text',
        observe: 'response'
      }
    ).pipe(
      map((response: HttpResponse<string>) => ProfilePictureLoadService.toProfilePicture(response)),
      catchError((error: HttpErrorResponse): Observable<ProfilePicture> => {
        if (error.status === 403 || error.status === 404) {
          return of(ProfilePictureLoadService.noPicture());
        }
        console.error(error);
        return throwError(() => error);
      }),
      tap((newState: ProfilePicture) => this.storeProfilePicture(userId, newState)),
      finalize(() => this.inFlight.delete(userId)),
      shareReplay({bufferSize: 1, refCount: false})
    );
    this.inFlight.set(userId, request);
    return request;
  }

  private storeProfilePicture(userId: string, newState: ProfilePicture): void {
    const state = this.profilePictureStore.getValue() as unknown as EntityState<ProfilePicture, string>;
    const current: ProfilePicture | undefined = state.entities?.[userId];
    if (!newState.base64) {
      if (!current?.base64?.startsWith('data:')) {
        this.profilePictureStore.upsert(userId, () => newState);
      }
      return;
    }
    if (current?.base64 && current.loaded) {
      this.preload(newState.base64,
        () => this.profilePictureStore.upsert(userId, () => ({...newState, loaded: true})),
        () => this.profilePictureStore.upsert(userId, () => ProfilePictureLoadService.noPicture()));
      return;
    }
    this.profilePictureStore.upsert(userId, () => newState);
    this.preload(newState.base64,
      () => {
        this.profilePictureStore.update(userId, {loaded: true});
      },
      () => this.profilePictureStore.upsert(userId, () => ProfilePictureLoadService.noPicture()));
  }

  private preload(url: string, onLoad: () => void, onError: () => void): void {
    const probe = new Image();
    probe.onload = onLoad;
    probe.onerror = onError;
    probe.src = url;
  }

}
